// notifications-daily-stack — WHAT A CUSTOMER DID, AS THE DESK READS IT.
//
// One event per thing a customer sends (Freddi, 2026-10-14: "all uploads … all
// should appear as notification bubble and bell"), written beside the source
// write and read by `GET /api/notifications`. The stack shows what is still
// WAITING on Patricia; the bell shows what is NEW. Both read this one shape and
// these words, so a bubble, a fan row and a bell row never describe one event
// two ways (decision `notifications-bubble-stays-until-resolved`).

import { BUSINESS_TZ } from "./dates";
import { stayDay } from "./messages";

export const NOTIFICATION_KINDS = ["request", "screenshot", "card", "registration"] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export interface DeskNotification {
  id: string;
  kind: NotificationKind;
  createdAt: string;
  readAt: string | null;
  /** The job is done — the request decided, the screenshot checked, the card answered — by anyone, anywhere. */
  resolved: boolean;
  bookingId: string | null;
  dogId: string | null;
  ownerId: string | null;
  proofId: string | null;
  /** First name of the person whose sign-in sent it; null when it cannot be told (a row older than 0042). */
  sender: string | null;
  /** The sender's full name and contact id — for the avatar, drawn as App access draws that person. */
  senderName: string | null;
  senderContactId: number | null;
  /** The household's first contact, for an event whose sender is not known. */
  household: string | null;
  /** The card's dog, or the stay's dogs, or the household's dogs for a registration. */
  dogs: string[];
  /** Which money line a screenshot is for. */
  line: "deposit" | "remainder" | null;
  stay: { checkIn: string; checkOut: string } | null;
}

/** `Luna`, `Luna and Pip`, `Luna, Pip and Rua`. */
export function joinDogs(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** `17–20 Oct`, or `31 Oct–3 Nov` across a month. */
export function stayRangeShort(stay: { checkIn: string; checkOut: string }): string {
  const a = stayDay(stay.checkIn, false);
  const b = stayDay(stay.checkOut, false);
  const [da, ma] = a.split(" ");
  const [db, mb] = b.split(" ");
  return ma === mb ? `${da}–${db} ${mb}` : `${a}–${b}`;
}

/**
 * ONE BUTTON FOR EVERY KIND (Freddi, 2026-10-14: "the button should be the same for all and should be
 * called open"). Was Answer / Check / See card / Open, from the prototype; the kind icon now says what
 * it is, so the button only says where it goes.
 */
export const NOTIFICATION_VERB = "Open";

/** The bubble's two lines and its button. */
export function bubbleWords(e: DeskNotification): { title: string; detail: string; verb: string } {
  const who = e.sender ?? e.household ?? "A customer";
  const dogs = joinDogs(e.dogs);
  const when = e.stay ? stayRangeShort(e.stay) : null;
  const withDogs = [dogs, when].filter(Boolean).join(" · ");
  switch (e.kind) {
    case "request":
      // "requested", the desk's own word for the status (Freddi, 2026-10-14: "x asked for a stay should be x requested a stay").
      return { title: `${who} requested a stay`, detail: withDogs, verb: NOTIFICATION_VERB };
    case "screenshot":
      return { title: `${who} sent the ${e.line === "remainder" ? "balance" : "deposit"} screenshot`, detail: withDogs, verb: NOTIFICATION_VERB };
    case "card":
      return { title: `${who} sent ${dogs ? `${dogs}’s` : "a"} card`, detail: when ?? "vaccination card", verb: NOTIFICATION_VERB };
    case "registration":
      return { title: `${who} finished registering`, detail: dogs, verb: NOTIFICATION_VERB };
  }
}

/** A registration asks nothing of Patricia: the bell's alone, never a bubble (AC10). */
export const waitsOnDesk = (e: DeskNotification): boolean => e.kind !== "registration" && !e.resolved;

const newestFirst = (a: DeskNotification, b: DeskNotification) => Date.parse(b.createdAt) - Date.parse(a.createdAt);

/** The stack: what waits, newest first. */
export const stackList = (events: readonly DeskNotification[]): DeskNotification[] => events.filter(waitsOnDesk).sort(newestFirst);

const WEEK_MS = 7 * 24 * 3_600_000;
/** The bell: everything unread, and what was read within the week (bell AC5), newest first. */
export const bellList = (events: readonly DeskNotification[], now = Date.now()): DeskNotification[] =>
  events.filter((e) => !e.readAt || now - Date.parse(e.readAt) < WEEK_MS).sort(newestFirst);

/** The fan's filter chips: screenshots are one job whether deposit or balance. */
export function notificationGroup(kind: NotificationKind): "Requests" | "Screenshots" | "Cards" | "Registrations" {
  return kind === "request" ? "Requests" : kind === "screenshot" ? "Screenshots" : kind === "card" ? "Cards" : "Registrations";
}

/** Bubble geometry from the prototype: 250px at least, 10px apart, a 92px deck. */
export const STACK_MIN_BUBBLE = 250;
export const STACK_GAP = 10;
export const STACK_DECK_W = 92;

/** How many bubbles the row holds — every one if they fit, else room for the deck (AC2). */
export function stackFit(rowWidth: number, count: number): number {
  if (count <= 0) return 0;
  if (count * (STACK_MIN_BUBBLE + STACK_GAP) <= rowWidth) return count;
  return Math.max(1, Math.floor((rowWidth - STACK_DECK_W - STACK_GAP) / (STACK_MIN_BUBBLE + STACK_GAP)));
}

const DAY_MS = 24 * 3_600_000;
const clock = new Intl.DateTimeFormat("en-IE", { timeZone: BUSINESS_TZ, hour: "2-digit", minute: "2-digit", hour12: false });

/** The time on a bubble — Dublin's clock — or, past a day, how long it has waited (AC8). */
export function waitingLabel(createdAt: string, now = Date.now()): { text: string; aged: boolean } {
  const age = now - Date.parse(createdAt);
  if (age < DAY_MS) return { text: clock.format(new Date(createdAt)), aged: false };
  const days = Math.floor(age / DAY_MS);
  return { text: `waiting ${days} day${days === 1 ? "" : "s"}`, aged: true };
}
